/**
 * Admin User Management Service
 * Centralises all API calls for the admin user management feature.
 * Re-exports from existing services where possible; adds sign-up endpoint.
 */
import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';
import {
  getAllUsers,
  getUserById,
  searchUsers,
  createUser,
  updateUser,
  deleteUser,
  enableUser,
  disableUser,
  updateUserPassword,
  getAvailableRoles,
  changeUserRole,
} from '../../../features/companysettings/services/userManagementService';
import {
  getAllModules,
  getCompanyRoles,
  getCompanyUsers,
  getUserPermissions,
  updateUserPermissions,
  bulkUpdateUserPermissions,
  createModule,
  updateModule,
  deleteModule,
} from '../../../features/access-management/services/accessManagementService';
import {
  getAllStores,
  createStore,
  updateStore,
  deleteStore,
  activateStore,
  deactivateStore,
  assignUserToStore,
  removeUserFromStore,
  getStoreUsers,
} from '../../../features/companysettings/services/storeUserService';

// ── Re-exports ─────────────────────────────────────────────────────────────
export {
  getAllUsers,
  getUserById,
  searchUsers,
  updateUser,
  deleteUser,
  enableUser,
  disableUser,
  updateUserPassword,
  getAvailableRoles,
  changeUserRole,
  getAllModules,
  getCompanyRoles,
  getCompanyUsers,
  getUserPermissions,
  updateUserPermissions,
  bulkUpdateUserPermissions,
  createModule,
  updateModule,
  deleteModule,
  getAllStores,
  createStore,
  updateStore,
  deleteStore,
  activateStore,
  deactivateStore,
  assignUserToStore,
  removeUserFromStore,
  getStoreUsers,
};

// ── Sign-up (platform-level create user) ──────────────────────────────────
export const signUpUser = async (payload) => {
  const response = await axiosInstance.post('/Account/sign-up', payload, {
    withCredentials: true,
  });
  return response.data;
};

// ── Update user email / username ──────────────────────────────────────────
export const updateUserEmail = async (userId, email) => {
  const response = await axiosInstance.put(`/Account/users/${userId}/email`, { email }, {
    withCredentials: true,
  });
  return response.data;
};

// ── Grouped user list (by company) ────────────────────────────────────────
export const getUsersGroupedByCompany = async () => {
  const response = await getAllUsers();
  const users = response?.payload || response?.data || response || [];
  const map = new Map();
  users.forEach((u) => {
    const key = u.CompanyName || u.companyName || 'No Company';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(u);
  });
  return map;
};

// ── Get all companies with summary information ───────────────────────────
export const getAllCompanies = async () => {
  const response = await axiosInstance.get('/Company/get-all-companies');
  return response.data?.payload || response.data || [];
};

// ── Companies with full metadata (derived from new API) ───────────────────
export const getCompaniesWithUsers = async () => {
  const companiesList = await getAllCompanies();
  
  // Map CompanyListItemDto to expected format
  return companiesList.map(company => ({
    id:              company.Id,
    name:            company.CompanyName || '',
    email:           company.Email || '',
    phoneNumber:     company.PhoneNumber || '',
    btwNumber:       company.BTWNumber || '',
    fullAddress:     company.FullAddress || '',
    storesCount:     company.StoresCount || 0,
    usersCount:      company.UsersCount || 0,
    subscriptionDate: company.SubscriptionDate,
    isActive:        company.IsActive !== undefined ? company.IsActive : true,
    // Create a mock users array with the count for compatibility
    users:           Array(company.UsersCount || 0).fill(null).map((_, i) => ({ id: i })),
  }));
};

// ── Company CRUD ──────────────────────────────────────────────────────────
export const getCompanyById = async (companyId) => {
  const response = await axiosInstance.get(`/Company/${companyId}`);
  return response.data?.payload || response.data;
};

export const getCompanyDetailStats = async (companyId) => {
  const response = await axiosInstance.get(`/Company/${companyId}/stats`);
  return response.data?.payload || response.data;
};

export const updateCompanyDetails = async (dto) => {
  const response = await axiosInstance.put('/Company/update-details', dto);
  return response.data?.payload || response.data;
};

export const updateCompany = async (companyId, data) => {
  const response = await axiosInstance.put('/Companies/update', { ...data, Id: companyId });
  return response.data;
};

export const deleteCompany = async (companyId) => {
  const response = await axiosInstance.delete(`/Companies/delete/${companyId}`);
  return response.data;
};

export const deactivateCompany = async (companyId, reason = '') => {
  const response = await axiosInstance.post(`/Company/${companyId}/deactivate`, {
    companyId,
    reason,
  });
  return response.data?.payload || response.data;
};

export const activateCompany = async (companyId) => {
  const response = await axiosInstance.post(`/Company/${companyId}/activate`);
  return response.data?.payload || response.data;
};

export const getCompanyStores = async (companyId) => {
  const response = await axiosInstance.get(`/Company/${companyId}/stores`);
  return response.data?.payload || response.data;
};

// ── Admin user actions ────────────────────────────────────────────────────
export const adminChangePassword = async (userId, newPassword, confirmPassword) => {
  const response = await axiosInstance.post('/Account/admin/change-password', {
    UserId: userId,
    NewPassword: newPassword,
    ConfirmPassword: confirmPassword,
  });
  return response.data;
};

export const adminChangeUsername = async (userId, newUsername) => {
  const response = await axiosInstance.post('/Account/admin/change-username', {
    UserId: userId,
    NewUsername: newUsername,
  });
  return response.data;
};

export const adminChangeEmail = async (userId, newEmail) => {
  const response = await axiosInstance.post('/Account/admin/change-email', {
    UserId: userId,
    NewEmail: newEmail,
  });
  return response.data;
};
